/**
 * DEALSHAQ DOCUMENTATION CONTENT
 * Phase 1: 8 Priority Coverage Areas (4 Retailer + 4 Consumer)
 * 
 * This file contains the comprehensive documentation content
 * following the finalized 11-section blueprint.
 * 
 * MULTILINGUAL POLICY:
 * - DealShaq Grocer App: English-only
 * - DealShaq Shopper App: Full multilingual (9 languages)
 * - Admin Portal: English-only
 * - DealShaq Charity App: English-only
 */

// =============================================================================
// PORTAL OVERVIEWS - Section II.A, III.A, IV.A, V.A
// =============================================================================
export const PORTAL_OVERVIEWS = {
  retailer: {
    id: "retailer-overview",
    title: "DealShaq Grocer App Overview",
    icon: "Store",
    description: "Everything you need to know about selling surplus inventory on DealShaq",
    languagePolicy: "English-only",
    sections: [
      {
        id: "what-retailer-portal-does",
        title: "What the DealShaq Grocer App Does",
        content: `The DealShaq Grocer App is your command center for turning surplus inventory into revenue while reducing food waste.

**Core Capabilities:**
- Post deals on near-date surplus items (N-DUSGF)
- Manage incoming orders with real-time notifications
- Track performance analytics and revenue
- Configure store settings, hours, and delivery options
- Connect with DoorDash for seamless delivery dispatch

**Who Uses It:**
- Store managers
- Inventory associates
- Business owners
- Authorized staff members`
      },
      {
        id: "how-retailers-make-money",
        title: "How Retailers Make Money",
        content: `DealShaq helps you recover value from surplus inventory that would otherwise go to waste.

**Revenue Model:**
| Your Discount | Platform Fee | You Receive |
|---------------|--------------|-------------|
| 60% off (Sizzling) | 10% | 90% of deal price |
| 75% off (Hot) | 15% | 85% of deal price |
| 90% off (Sizzling Hot) | 10% | 90% of deal price |

**Example:**
- Regular price: $10.00
- You set: Hot Deal (75% off to DealShaq)
- Consumer pays: $4.00 (60% off displayed)
- Platform fee: $0.60
- You receive: $3.40

**Better than waste:**
Instead of $0 from discarded inventory, you recover 30-50% of retail value while helping the environment.`
      },
      {
        id: "understanding-ndusagf",
        title: "Understanding N-DUSGF",
        content: `**N-DUSGF** = Near-Date Unsold Surplus Grocery Food

This is the core of what DealShaq handles - perfectly good food items approaching their expiration dates that retailers need to move quickly.

**Why It Matters:**
- 40% of food in the US goes to waste
- Much of this is near-date but perfectly safe
- Traditional discounting is inefficient
- DealShaq connects surplus directly to deal-seeking consumers

**What Qualifies:**
- Items within 1-7 days of expiration
- Surplus inventory from over-ordering
- Seasonal items past peak demand
- Cosmetically imperfect but safe products`
      },
      {
        id: "retailer-language-policy",
        title: "Language Support",
        content: `**The DealShaq Grocer App is English-only.**

This includes:
- All user interface elements
- Dashboard and analytics
- Order management screens
- Settings and configuration
- Push notifications
- Email communications
- Documentation and help content

**Why English-only:**
- Operational consistency across all partner stores
- Accurate barcode/product data handling
- Clear communication in time-sensitive order workflows
- Standardized training and support

**Note:** Customer-facing product names and descriptions are entered as-is and displayed to consumers without translation.`
      }
    ]
  },
  consumer: {
    id: "consumer-overview",
    title: "DealShaq Shopper App Overview",
    icon: "ShoppingCart",
    description: "Your guide to finding sizzling hot deals and saving money on groceries",
    languagePolicy: "Multilingual (9 languages)",
    sections: [
      {
        id: "what-consumer-portal-does",
        title: "What the DealShaq Shopper App Does",
        content: `The DealShaq Shopper App connects you with deep discounts on quality food from local retailers.

**Core Features:**
- Browse deals from nearby stores (50-75% off)
- AI-powered shopping assistant (DealShaq Shopping Assistant (DSA))
- Multi-store cart management
- Pickup or delivery options
- Order tracking and notifications
- Savings and impact tracking

**Save Money, Help the Planet:**
Every purchase helps reduce food waste while giving you access to premium groceries at a fraction of the price.`
      },
      {
        id: "how-dealshaq-saves-money",
        title: "How DealShaq Saves You Money",
        content: `DealShaq partners with local retailers to offer their surplus inventory at deep discounts.

**Discount Levels:**
| Badge | Discount | Best For |
|-------|----------|----------|
| <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> Sizzling | 50% off | Items with 5+ days left |
| <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> Hot | 60% off | Items with 3-5 days left |
| <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> Sizzling Hot | 75% off | Items expiring in 1-2 days |

**Real Savings Example:**
- Weekly grocery budget: $150
- Same items via DealShaq: $60-75
- Weekly savings: $75-90
- Annual savings: $3,900-4,680

**Quality Guarantee:**
All items are within their sell-by dates and meet food safety standards.`
      },
      {
        id: "consumer-language-support",
        title: "Language Support",
        content: `**The DealShaq Shopper App supports full multilingual UI.**

**Supported Languages:**
- English
- Spanish (Español)
- French (Français)
- German (Deutsch)
- Italian (Italiano)
- Portuguese (Português)
- Arabic (العربية) - RTL supported
- Chinese (中文)
- Korean (한국어)

**How to Change Language:**
1. Go to Settings
2. Click "Language"
3. Select your preferred language
4. Interface updates immediately

**What Gets Translated:**
- All UI elements (buttons, labels, menus)
- System messages and notifications
- Help documentation
- AI Agent responses

**What Stays Original:**
- Product names (as entered by retailers)
- Store names and addresses
- User-generated content`
      }
    ]
  },
  admin: {
    id: "admin-overview",
    title: "Admin Portal Overview",
    icon: "Shield",
    description: "Platform management, approvals, and monitoring",
    languagePolicy: "English-only",
    sections: [
      {
        id: "admin-purpose",
        title: "Purpose of the Admin Portal",
        content: `The Admin Portal is the control center for DealShaq platform operations.

**Key Responsibilities:**
- Retailer approval and management
- Charity partner approval
- Platform health monitoring
- Incident management and resolution
- Agent telemetry and performance tracking
- Manual overrides when needed

**Access Control:**
Admin access is restricted to authorized DealShaq staff only.`
      },
      {
        id: "admin-language-policy",
        title: "Language Support",
        content: `**The Admin Portal is English-only.**

All administrative functions, reports, and communications are conducted in English to ensure:
- Consistent incident documentation
- Clear audit trails
- Standardized approval workflows
- Accurate platform metrics reporting`
      }
    ]
  },
  charity: {
    id: "charity-overview",
    title: "DealShaq Charity App Overview",
    icon: "Heart",
    description: "Partner with DealShaq to receive food donations and track impact",
    languagePolicy: "English-only",
    sections: [
      {
        id: "charity-partnership",
        title: "How DealShaq Charity Partnerships Work",
        content: `DealShaq connects charity partners with food donations from our retail network.

**How It Works:**
1. Consumers can round up purchases to donate
2. Retailers can contribute unsold items
3. Charities receive donations and track impact

**Benefits:**
- Regular food donations
- Impact tracking and reporting
- Connection to local retail partners
- Visibility to community supporters`
      },
      {
        id: "charity-language-policy",
        title: "Language Support",
        content: `**The DealShaq Charity App is English-only.**

All charity communications, reports, and documentation are provided in English.`
      }
    ]
  }
};

// =============================================================================
// MULTILINGUAL SUPPORT - Section XI
// =============================================================================
export const MULTILINGUAL_DOCS = {
  consumer: {
    id: "consumer-multilingual",
    title: "Consumer Multilingual Support",
    icon: "Globe",
    description: "Full multilingual UI for consumers",
    sections: [
      {
        id: "language-switcher",
        title: "Language Switcher",
        content: `**How to Change Your Language:**

1. Click the **Settings** icon in the menu
2. Find the **Language** section
3. Select your preferred language from the dropdown
4. The interface updates immediately

**Supported Languages:**
- English (Default)
- Spanish (Español)
- French (Français)
- German (Deutsch)
- Italian (Italiano)
- Portuguese (Português)
- Arabic (العربية)
- Chinese (中文)
- Korean (한국어)

**RTL Support:**
Arabic language includes full right-to-left layout support.`
      },
      {
        id: "ai-agent-multilingual",
        title: "AI Agent Multilingual Behavior",
        content: `The DealShaq Shopping Assistant (DSA) responds in your preferred language.

**How It Works:**
- Agent detects your language from your message
- Responds in the same language
- Remembers your preference for future conversations

**Supported Languages for AI:**
All 9 languages supported in the UI are also supported by the AI Agent.

**Example:**
- You: "Busco huevos orgánicos"
- Agent: "Encontré 3 ofertas de huevos orgánicos cerca de ti..."

**Language Detection:**
The agent automatically detects your language. You don't need to set anything - just write in your preferred language.`
      },
      {
        id: "what-gets-translated",
        title: "What Gets Translated",
        content: `**Translated Elements:**
- Navigation menus
- Buttons and labels
- Form fields and placeholders
- Error messages
- Success notifications
- Help text and tooltips
- AI Agent responses
- Order status messages

**Not Translated:**
- Product names (as entered by retailers)
- Store names and addresses
- User reviews and comments
- Receipt details from stores
- Third-party content (DoorDash messages)`
      },
      {
        id: "known-gaps",
        title: "Known Limitations",
        content: `**Current Limitations:**

1. **Product Names**: Displayed as entered by retailers (usually English)
2. **Store Addresses**: Not translated for accuracy
3. **Email Receipts**: Currently English-only
4. **Push Notifications**: Follow UI language setting
5. **External Links**: May lead to English content

**Roadmap:**
- Product name translation (planned)
- Receipt localization (planned)
- Additional languages (based on demand)`
      }
    ]
  },
  retailer: {
    id: "retailer-multilingual",
    title: "Retailer Language Policy",
    icon: "Globe",
    description: "English-only for operational consistency",
    sections: [
      {
        id: "english-only-policy",
        title: "English-Only Policy",
        content: `**The DealShaq Grocer App is English-only.**

**What This Means:**
- All UI elements are in English
- No language switcher is available
- All notifications are in English
- Documentation is in English only

**Why This Policy:**
- Operational consistency across all partner stores
- Time-sensitive order workflows require clear communication
- Barcode and product data systems use English
- Standardized training materials
- Easier troubleshooting and support`
      },
      {
        id: "retailer-content-handling",
        title: "Content You Enter",
        content: `**Retailer-Entered Content:**

Content you enter (product names, descriptions, store info) is displayed to consumers exactly as entered.

**Recommendations:**
- Use clear, simple product names
- Avoid abbreviations
- Include key details (size, quantity)
- Consumer AI can help shoppers understand products

**Example:**
You enter: "Organic Eggs, Large, 12-pack"
Consumer sees: "Organic Eggs, Large, 12-pack"
(Not translated, displayed as-is)`
      },
      {
        id: "ai-agent-retailer-policy",
        title: "AI Agent Policy",
        content: `**When You Ask the AI Agent:**

If you ask the AI Agent a question about the DealShaq Grocer App, it will respond in English only.

**Example:**
- You: "¿Cómo publico un artículo?"
- Agent: "To post an item on DealShaq, follow these steps... (The DealShaq Grocer App documentation is available in English only.)"

This ensures consistent, accurate guidance for all operational questions.`
      }
    ]
  }
};

// =============================================================================
// GLOSSARY - Shared Terms (Section VI)
// =============================================================================
export const GLOSSARY = {
  // Core Platform Terms
  "RSHD": {
    term: "RSHD",
    definition: "Retailer Sizzling Hot Deal - An item posted by a retailer at 50-75% off regular price",
    category: "Platform",
    relatedTerms: ["Deal Level", "DRLP"]
  },
  "DRLP": {
    term: "DRLP",
    definition: "DealShaq Retailer Location Partner - A retailer partner store on the platform",
    category: "Platform",
    relatedTerms: ["RSHD", "N-DUSGF"]
  },
  "DAC": {
    term: "DAC",
    definition: "DealShaq App Consumer - A registered consumer/shopper on the platform",
    category: "Platform",
    relatedTerms: ["DRLP"]
  },
  "DACSAI": {
    term: "DACSAI",
    definition: "DAC's Shopping Area of Interest - The circular area (delivery-location center + adjustable radius of 0.1-9.9 miles) that determines which retailers a consumer sees and follows",
    category: "Platform",
    relatedTerms: ["DAC", "DACDRLP-List", "DRLP"]
  },
  "DACDRLP-List": {
    term: "DACDRLP-List",
    definition: "DAC's DRLP List - A consumer's list of retailers they follow and get deal alerts from, auto-filled from their DACSAI with manual add/remove overrides",
    category: "Platform",
    relatedTerms: ["DAC", "DACSAI", "DRLP"]
  },
  "DACFI-List": {
    term: "DACFI-List",
    definition: "DAC Favorite Items List - A consumer's list of favorite items, brands, and categories used to personalize which deals trigger notifications",
    category: "Platform",
    relatedTerms: ["DAC", "DACDRLP-List", "RSHD"]
  },
  "N-DUSGF": {
    term: "N-DUSGF",
    definition: "Near-Date Unsold Surplus Grocery Food - Food items approaching expiration that retailers sell at deep discounts",
    category: "Platform",
    relatedTerms: ["RSHD"]
  },
  
  // Deal Levels
  "Sizzling": {
    term: "Sizzling Deal",
    definition: "50% off regular price - Entry level discount tier",
    category: "Deals",
    relatedTerms: ["Hot Deal", "Sizzling Hot Deal"]
  },
  "Hot": {
    term: "Hot Deal",
    definition: "60% off regular price - Mid-tier discount",
    category: "Deals",
    relatedTerms: ["Sizzling Deal", "Sizzling Hot Deal"]
  },
  "Sizzling Hot": {
    term: "Sizzling Hot Deal",
    definition: "75% off regular price - Maximum discount tier for items closest to expiration",
    category: "Deals",
    relatedTerms: ["Sizzling Deal", "Hot Deal"]
  },

  // Order Terms
  "Acknowledgment": {
    term: "Order Acknowledgment",
    definition: "When a retailer confirms they received an order and commits to a ready-by time (must be done within 5 minutes)",
    category: "Orders",
    relatedTerms: ["Ready-By Time", "Auto-Cancel"]
  },
  "Ready-By Time": {
    term: "Ready-By Time",
    definition: "The time by which the retailer commits to have an order prepared for pickup or delivery",
    category: "Orders",
    relatedTerms: ["Acknowledgment", "Mark Ready"]
  },
  "Mark Ready": {
    term: "Mark Ready",
    definition: "When a retailer indicates an order is prepared and triggers payment capture",
    category: "Orders",
    relatedTerms: ["Payment Capture", "Dispatch"]
  },

  // Scanner Terms
  "PLU": {
    term: "PLU Code",
    definition: "Price Look-Up code - 4-5 digit codes on produce stickers. Codes starting with 4 are conventional, 9 are organic",
    category: "Scanning",
    relatedTerms: ["UPC", "Barcode"]
  },
  "UPC": {
    term: "UPC",
    definition: "Universal Product Code - Standard barcode found on packaged goods",
    category: "Scanning",
    relatedTerms: ["PLU", "EAN"]
  },
  "OCR": {
    term: "OCR",
    definition: "Optical Character Recognition - Technology that extracts text from images (used for price tags, labels)",
    category: "Scanning",
    relatedTerms: ["Barcode"]
  },

  // AI Terms
  "DealShaq Shopping Assistant (DSA)": {
    term: "DealShaq Shopping Assistant (DSA)",
    definition: "AI-powered shopping assistant that understands natural language requests and finds matching deals",
    category: "AI",
    relatedTerms: ["NLU", "Memory"]
  },
  "NLU": {
    term: "NLU",
    definition: "Natural Language Understanding - AI technology that interprets your requests in plain language",
    category: "AI",
    relatedTerms: ["DealShaq Shopping Assistant (DSA)"]
  },
  "Memory": {
    term: "Agent Memory",
    definition: "The AI assistant's ability to remember your preferences, dietary restrictions, and past interactions",
    category: "AI",
    relatedTerms: ["DealShaq Shopping Assistant (DSA)", "Short-term Memory", "Long-term Memory"]
  },

  // Shopping List Sourcing Intents
  "shopping_list.create": {
    term: "shopping_list.create",
    definition: "Intent triggered when a consumer provides a new grocery shopping list to the AI Agent",
    category: "AI Intents",
    relatedTerms: ["shopping_list.sourcing", "shopping_list.constraints"]
  },
  "shopping_list.add_items": {
    term: "shopping_list.add_items",
    definition: "Intent for adding additional items to an existing shopping list",
    category: "AI Intents",
    relatedTerms: ["shopping_list.remove_items", "shopping_list.create"]
  },
  "shopping_list.remove_items": {
    term: "shopping_list.remove_items",
    definition: "Intent for removing items from a shopping list",
    category: "AI Intents",
    relatedTerms: ["shopping_list.add_items"]
  },
  "shopping_list.constraints": {
    term: "shopping_list.constraints",
    definition: "Intent for setting constraints on shopping list sourcing (budget, dietary, stores, freshness)",
    category: "AI Intents",
    relatedTerms: ["shopping_list.budget", "shopping_list.dietary", "shopping_list.brand_preferences"]
  },
  "shopping_list.sourcing": {
    term: "shopping_list.sourcing",
    definition: "Intent triggered when the AI Agent begins searching stores to fulfill a shopping list",
    category: "AI Intents",
    relatedTerms: ["shopping_list.multi_store", "shopping_list.optimize"]
  },
  "shopping_list.optimize": {
    term: "shopping_list.optimize",
    definition: "Intent for re-optimizing a sourced list (cheapest, fewest stores, organic only, etc.)",
    category: "AI Intents",
    relatedTerms: ["shopping_list.sourcing", "shopping_list.review"]
  },
  "shopping_list.review": {
    term: "shopping_list.review",
    definition: "Intent triggered when consumer wants to review the sourced shopping list before approval",
    category: "AI Intents",
    relatedTerms: ["shopping_list.finalize", "shopping_list.substitutions"]
  },
  "shopping_list.substitutions": {
    term: "shopping_list.substitutions",
    definition: "Intent for handling item substitutions when requested items are unavailable",
    category: "AI Intents",
    relatedTerms: ["shopping_list.sourcing", "shopping_list.review"]
  },
  "shopping_list.multi_store": {
    term: "shopping_list.multi_store",
    definition: "Intent for sourcing items across multiple stores to fulfill a complete shopping list",
    category: "AI Intents",
    relatedTerms: ["shopping_list.sourcing", "shopping_list.optimize"]
  },
  "shopping_list.budget": {
    term: "shopping_list.budget",
    definition: "Intent for applying budget constraints to shopping list sourcing",
    category: "AI Intents",
    relatedTerms: ["shopping_list.constraints", "shopping_list.optimize"]
  },
  "shopping_list.dietary": {
    term: "shopping_list.dietary",
    definition: "Intent for applying dietary restrictions (halal, kosher, vegan, etc.) to shopping list",
    category: "AI Intents",
    relatedTerms: ["shopping_list.constraints"]
  },
  "shopping_list.brand_preferences": {
    term: "shopping_list.brand_preferences",
    definition: "Intent for applying brand preferences (organic, specific brands) to sourcing",
    category: "AI Intents",
    relatedTerms: ["shopping_list.constraints"]
  },
  "shopping_list.expiration": {
    term: "shopping_list.expiration",
    definition: "Intent for applying freshness/expiration rules to sourced items",
    category: "AI Intents",
    relatedTerms: ["shopping_list.constraints"]
  },
  "shopping_list.finalize": {
    term: "shopping_list.finalize",
    definition: "Intent triggered when consumer approves the sourced list and creates the final cart",
    category: "AI Intents",
    relatedTerms: ["shopping_list.review"]
  },
  "shopping_list.upload": {
    term: "shopping_list.upload",
    definition: "Intent triggered when consumer uploads a file (txt, csv, image, pdf) containing a shopping list for OCR extraction",
    category: "AI Intents",
    relatedTerms: ["shopping_list.create", "shopping_list.sourcing"]
  },

  // Documentation Help Intents
  "docs.topic_lookup": {
    term: "docs.topic_lookup",
    definition: "Intent for looking up a specific documentation topic by name or keyword",
    category: "AI Intents - Documentation",
    relatedTerms: ["docs.how_to", "docs.definition"]
  },
  "docs.how_to": {
    term: "docs.how_to",
    definition: "Intent for step-by-step instructions on how to perform a specific action or workflow",
    category: "AI Intents - Documentation",
    relatedTerms: ["docs.topic_lookup", "docs.error_help"]
  },
  "docs.definition": {
    term: "docs.definition",
    definition: "Intent for requesting the definition of a platform term, acronym, or concept",
    category: "AI Intents - Documentation",
    relatedTerms: ["docs.topic_lookup", "Glossary"]
  },
  "docs.error_help": {
    term: "docs.error_help",
    definition: "Intent for getting help with a specific error message or troubleshooting a problem",
    category: "AI Intents - Documentation",
    relatedTerms: ["docs.how_to", "docs.topic_lookup"]
  },

  // Consumer Multilingual Intents
  "change_language": {
    term: "change_language",
    definition: "Intent for changing the UI language (Consumer portal only - supports 9 languages)",
    category: "AI Intents - Multilingual",
    relatedTerms: ["translation_issue", "ai_language_request"]
  },
  "translation_issue": {
    term: "translation_issue",
    definition: "Intent for reporting a translation problem or requesting clarification on translated content",
    category: "AI Intents - Multilingual",
    relatedTerms: ["change_language", "ai_language_request"]
  },
  "ai_language_request": {
    term: "ai_language_request",
    definition: "Intent for requesting the AI Agent to respond in a specific language (Consumer portal only)",
    category: "AI Intents - Multilingual",
    relatedTerms: ["change_language", "translation_issue"]
  },

  // Language Policy
  "RETAILER_LANGUAGE_POLICY": {
    term: "RETAILER_LANGUAGE_POLICY",
    definition: "Enforcement rule: Retailer, Admin, and Charity portals are English-only. AI Agent must respond in English for any queries related to these portals, even if the user writes in another language.",
    category: "AI Intents - Policy",
    relatedTerms: ["change_language"]
  }
};

// =============================================================================
// RETAILER PRIORITY COVERAGE AREAS (4)
// =============================================================================
export const RETAILER_PRIORITY_DOCS = {
  // Priority 1: Barcode Scanning
  "barcode-scanning": {
    id: "retailer-barcode-scanning",
    title: "Barcode Scanning & Capture System",
    icon: "ScanBarcode",
    priority: 1,
    estimatedTime: "10 min read",
    description: "Master all 7 scanning modes and OCR capabilities",
    sections: [
      {
        id: "scan-modes-overview",
        title: "7 Scan Modes Overview",
        content: `DealShaq offers 7 different ways to capture product information, each optimized for different scenarios:

**1. Single Scan (Default)**
Best for: Individual items, careful verification
- Enter barcode manually or use keyboard scanner
- Review each product before adding to queue
- Ideal for high-value items or unfamiliar products

**2. Continuous Scan**
Best for: Rapid scanning of multiple items
- Camera stays active between scans
- Auto-advances after each successful scan
- Perfect for shelf clearing operations

**3. Bluetooth/USB Scanner**
Best for: High-volume operations with hardware scanner
- Connect any standard barcode scanner
- Fastest input method for experienced users
- Supports rapid-fire scanning

**4. Shelf Photo (Single Image)**
Best for: Quick shelf captures
- Take one photo of shelf section
- AI identifies multiple barcodes
- Good for sparse shelves

**5. Multi-Image Upload**
Best for: Comprehensive shelf documentation
- Upload multiple photos at once
- AI processes all images in batch
- Ideal for full inventory sweeps

**6. Video Scan**
Best for: Walking through aisles
- Record video panning across shelves
- AI extracts frames and identifies products
- Most comprehensive capture method

**7. Batch Mode**
Best for: Pre-prepared lists
- Enter multiple barcodes at once
- Process as a single batch
- Good for receiving shipments`
      },
      {
        id: "ocr-extraction",
        title: "OCR Price & Label Extraction",
        content: `The OCR system can extract information from price tags and product labels:

**What OCR Can Capture:**
- Regular price
- Sale price
- Weight/quantity
- Expiration dates
- Product names (partial)

**Best Practices for OCR:**
1. Ensure good lighting (avoid shadows)
2. Hold camera steady (no blur)
3. Capture full price tag in frame
4. Avoid glare from plastic covers
5. Use flash in dim conditions

**When OCR Fails:**
- Handwritten prices won't work
- Damaged/faded labels may fail
- Always verify extracted data before posting`
      },
      {
        id: "queue-system",
        title: "Queue System & Batch Posting",
        content: `The queue system requires a minimum of 2 items before posting:

**Why Minimum 2 Items?**
- Reduces accidental single-item posts
- Encourages batch efficiency
- Improves inventory management

**Queue Operations:**
- **Add**: Scan or enter items to add to queue
- **Review**: Check all items before posting
- **Edit**: Modify price, quantity, category
- **Remove**: Delete items from queue
- **Clear**: Empty entire queue
- **Post**: Submit all items as RSHDs

**Queue Best Practices:**
1. Review every item before posting
2. Verify prices match physical tags
3. Check expiration dates are entered
4. Ensure quantities are accurate
5. Use Demo Mode to practice first`
      },
      {
        id: "error-handling",
        title: "Error Handling & Troubleshooting",
        content: `Common scanning issues and solutions:

**Invalid Barcode**
- Verify barcode is complete and undamaged
- Try manual entry if scan fails
- Check if barcode type is supported

**OCR Mismatch**
- Always verify OCR-extracted prices
- Re-capture with better lighting
- Manually correct any errors

**Duplicate Detection**
- System warns if barcode already scanned
- Choose to update quantity or skip
- Review duplicates before posting

**Expired Items Rejected**
- Items with past expiration dates are blocked
- Verify expiration date is correct
- Contact support if date is wrong

**Camera Permission Issues**
- Enable camera in browser settings
- Try a different browser
- Use manual entry as fallback`
      },
      {
        id: "best-practices",
        title: "Scanning Best Practices",
        content: `Tips for efficient, accurate scanning:

**Lighting**
- Natural light is best
- Avoid direct overhead lights (causes glare)
- Use flash sparingly

**Angles**
- Hold phone parallel to barcode
- Avoid extreme angles
- Keep steady (use both hands)

**Workflow Efficiency**
1. Group similar items together
2. Prepare items before scanning session
3. Use Continuous mode for speed
4. Batch similar categories
5. Post immediately after scanning

**Quality Control**
- Spot-check 10% of scanned items
- Verify prices against shelf tags
- Double-check expiration dates
- Review queue before final post`
      }
    ],
    faqs: [
      {
        question: "What barcode types are supported?",
        answer: "UPC-A, UPC-E, EAN-13, EAN-8, Code 128, Code 39, and PLU codes (4-5 digits)."
      },
      {
        question: "Why does my scan sometimes fail?",
        answer: "Common causes: poor lighting, damaged barcode, camera out of focus, or unsupported barcode format."
      },
      {
        question: "Can I scan items without barcodes?",
        answer: "Yes! Use manual entry mode or PLU lookup for produce items."
      },
      {
        question: "What is Demo Mode?",
        answer: "Demo Mode pre-fills the queue with sample products so you can practice the posting workflow without affecting real inventory."
      }
    ]
  },

  // Priority 2: Item Posting Workflow
  "item-posting": {
    id: "retailer-item-posting",
    title: "Item Posting Workflow",
    icon: "Package",
    priority: 2,
    estimatedTime: "8 min read",
    description: "Master the 3-step posting flow for deals",
    sections: [
      {
        id: "three-step-flow",
        title: "3-Step Posting Flow",
        content: `Every RSHD follows a simple 3-step posting process:

**Step 1: Product Identification**
- Scan barcode (camera, image, or manual)
- System looks up product in database
- Auto-fills name, category, image if found
- Manual entry for unknown products

**Step 2: Price Capture**
- Upload photo of price tag (OCR extraction)
- Or enter price manually
- System validates price is reasonable
- Original retail price required

**Step 3: Deal Configuration**
- Select discount level (50%, 60%, or 75%)
- Enter quantity available (minimum 5)
- Set or verify expiration date
- Review and add to queue

**Final Step: Post Queue**
- Review all items in queue
- Minimum 2 items required
- Click "Post All Deals"
- Items immediately visible to consumers`
      },
      {
        id: "discount-levels",
        title: "Discount Level System",
        content: `DealShaq uses a fixed 3-tier discount system:

| Level | Your Discount | Consumer Sees | Best For |
|-------|---------------|---------------|----------|
| Sizzling | 60% off | 50% off | Items with 5+ days shelf life |
| Hot | 75% off | 60% off | Items with 3-5 days shelf life |
| Sizzling Hot | 90% off | 75% off | Items expiring in 1-2 days |

**How It Works:**
- You set the discount you're willing to give
- DealShaq adds a small margin for platform costs
- Consumer sees a clear, attractive discount

**Example:**
- Regular price: $10.00
- You select "Hot" (75% discount to DealShaq)
- DealShaq pays you: $2.50
- Consumer pays: $4.00 (60% off)
- Platform margin: $1.50

**Choosing the Right Level:**
- Consider shelf life remaining
- Factor in storage costs
- Balance speed of sale vs revenue`
      },
      {
        id: "quantity-rules",
        title: "Quantity & Stock Rules",
        content: `Important rules for posting quantities:

**Minimum Quantity: 5 units**
- Each RSHD must have at least 5 units
- Prevents single-item posts
- Ensures deal availability

**Stock Updates:**
- Inventory decrements automatically with orders
- System sends alerts at 4 units remaining
- You can manually update quantities anytime
- Zero stock = item hidden from consumers

**Restocking:**
- Don't edit existing RSHD quantities
- Post a NEW RSHD for fresh stock
- Old RSHD auto-hides when depleted

**Low Stock Alerts:**
- Notification when quantity ≤ 4
- Option to post new RSHD
- Prevents consumer disappointment`
      },
      {
        id: "categories",
        title: "Category Selection",
        content: `DealShaq uses a 20-category taxonomy:

**Food Categories:**
1. Baby & Kids
2. Bakery & Bread
3. Beverages
4. Breakfast & Cereal
5. Dairy & Eggs
6. Deli & Prepared Foods
7. Frozen Foods
8. Fruits
9. Health & Nutrition
10. Meat & Poultry
11. Oils, Sauces & Spices
12. Pantry Staples
13. Pasta, Rice & Grains
14. Seafood
15. Snacks & Candy
16. Vegetables

**Non-Food Categories:**
17. Household Essentials
18. Personal Care
19. Pet Supplies
20. Miscellaneous

**Why Correct Categories Matter:**
- Consumer search accuracy
- AI recommendation matching
- Analytics and reporting
- Dietary filter functionality`
      },
      {
        id: "expiration-rules",
        title: "Expiration Date Rules",
        content: `Expiration handling is critical for food safety:

**Requirements:**
- Expiration date is REQUIRED for perishables
- Optional for shelf-stable items
- Date must be in the future (today rejected)

**Date Formats Accepted:**
- MM/DD/YYYY
- YYYY-MM-DD
- Written dates (e.g., "Dec 25, 2024")

**Validation Rules:**
- Past dates: REJECTED (item blocked)
- Today's date: REJECTED
- Future dates: ACCEPTED

**Best Practices:**
1. Check physical product for date
2. Use earliest date if multiple shown
3. "Best By" vs "Use By" both accepted
4. When in doubt, use earlier date

**Items Without Expiration:**
- Canned goods (use "Best By")
- Dried goods (check package)
- Frozen items (note freezer date)
- Mark as N/A only if truly no date`
      },
      {
        id: "safety-quality",
        title: "Safety & Quality Requirements",
        content: `Quality standards for posted items:

**Required Conditions:**
- Packaging intact (no tears, punctures)
- Labels readable and attached
- No visible damage or contamination
- Proper temperature maintained

**Prohibited Items:**
- Expired products (past date)
- Recalled items
- Damaged packaging
- Temperature-abused products
- Previously opened items

**Quality Indicators:**
- Fresh appearance (produce)
- No bloating (packaged goods)
- Proper color (meat, dairy)
- No off-odors

**Liability:**
- Retailers responsible for item quality
- DealShaq reserves right to suspend accounts
- Consumer complaints investigated
- Repeated issues = account review`
      }
    ],
    faqs: [
      {
        question: "Can I change a price after posting?",
        answer: "Yes, navigate to Inventory, find the item, and click Edit. Price changes take effect immediately."
      },
      {
        question: "What happens when an item sells out?",
        answer: "The item automatically hides from consumers. You can post a new RSHD when you restock."
      },
      {
        question: "Can I post items without barcodes?",
        answer: "Yes! Use manual entry and provide a clear description. PLU codes work for produce."
      },
      {
        question: "Why is there a minimum quantity of 5?",
        answer: "This ensures deals have sufficient availability and prevents consumer disappointment from single-unit posts."
      }
    ]
  },

  // Priority 3: Order Management
  "order-management": {
    id: "retailer-order-management",
    title: "Order Management",
    icon: "ShoppingCart",
    priority: 3,
    estimatedTime: "12 min read",
    description: "Master the order lifecycle from receipt to completion",
    sections: [
      {
        id: "order-states",
        title: "Order State Machine",
        content: `Orders flow through defined states:

**State Flow:**
\`\`\`
pending_drlp_ack → drlp_confirmed → preparing → ready → dispatched → delivered
                                                    ↘ picked_up (if pickup)
\`\`\`

**States Explained:**

| State | Meaning | Your Action |
|-------|---------|-------------|
| pending_drlp_ack | New order, awaiting your response | Acknowledge within 5 min |
| resent_to_drlp | Re-sent (you missed first notification) | Acknowledge IMMEDIATELY |
| drlp_confirmed | You confirmed ready-by time | Start preparing |
| preparing | You're gathering items | Continue preparation |
| ready | Order is prepared | Mark Ready (triggers payment) |
| ready_overdue | Past ready-by time | Mark Ready NOW or cancel |
| dispatched | Driver assigned (delivery) | Wait for pickup |
| delivered | Customer received order | Complete |
| picked_up | Customer collected (pickup) | Complete |
| cancelled_* | Order cancelled | Review reason |`
      },
      {
        id: "key-actions",
        title: "Key Actions",
        content: `Actions you can take on orders:

**1. Acknowledge Order**
- Required within 5 minutes of receipt
- Select ready-by time (up to +30 min from requested)
- Confirms you'll fulfill the order
- Failure to acknowledge = order resent, then cancelled

**2. Start Preparing**
- Indicates you're actively working on order
- Optional but recommended for tracking
- Updates customer status view

**3. Mark Ready**
- CRITICAL: Triggers payment capture
- Only mark when order is fully prepared
- Notifies customer for pickup
- For delivery: enables dispatch

**4. Dispatch via DoorDash**
- Available only for delivery orders
- Only after order is marked Ready
- Enter store phone for driver contact
- Track delivery in real-time

**5. Cancel Order**
- Use only when absolutely necessary
- Must provide reason
- Releases payment hold
- Affects your reliability score`
      },
      {
        id: "timeout-rules",
        title: "Timeout Rules",
        content: `Strict timing rules protect consumers:

**Acknowledgment Timeout (5 minutes)**
- Order arrives → 5 min to acknowledge
- No response → Order RESENT with urgent flag
- Still no response after +5 min → Order CANCELLED
- Cancellation reason: "Store did not respond"

**Ready-By Deadline**
- You commit to a ready-by time when acknowledging
- Can delay up to 30 minutes from customer request
- Must mark ready by committed time
- Overdue orders flagged for immediate action

**Overdue Escalation**
- Past ready-by time → Status changes to "ready_overdue"
- Continued delay → Order cancelled
- Refund issued to customer
- Incident logged for review

**Why Timeouts Matter:**
- Customers plan around delivery times
- Payment holds expire
- Platform reliability depends on it
- Repeated timeouts = account review`
      },
      {
        id: "substitutions",
        title: "Substitutions & Out-of-Stock",
        content: `Handling inventory issues:

**When Item is Out of Stock:**
1. Do NOT just cancel the item
2. Check for suitable substitution
3. Contact customer if possible
4. Offer equivalent or better item
5. Adjust order if needed

**Substitution Guidelines:**
- Same category preferred
- Equal or better quality
- Same or lower price
- Notify customer of change

**Communication:**
- Use order notes for messages
- Be proactive about issues
- Apologize for inconvenience
- Offer compensation if appropriate

**Partial Fulfillment:**
- Fulfill what you have
- Adjust order total
- Customer charged only for received items
- Note missing items in order`
      },
      {
        id: "emergency-procedures",
        title: "Emergency Procedures",
        content: `Handling critical situations:

**Payment Capture Failure**
1. Order shows "Payment Error"
2. Do NOT hand over items
3. Contact support immediately
4. Wait for confirmation before release
5. Customer may need to retry payment

**Delivery Emergency**
1. Driver no-show: Contact DoorDash support
2. Wrong address: Update and re-dispatch
3. Customer unreachable: Hold order, notify support
4. Damaged in transit: Document and refund

**Store Closure Mid-Order**
1. Emergency closure: Cancel pending orders
2. Planned closure: Don't accept orders near closing
3. Power outage: Contact support for bulk cancellation
4. Staff shortage: Extend ready times or cancel

**System Outage**
1. Note order details offline
2. Contact support via phone
3. Manually track completions
4. Sync when system returns

**Contact Support:**
- Emergency: support@dealshaq.com
- Phone: Available in Settings
- In-app chat: Click Help icon`
      },
      {
        id: "avoiding-missed-orders",
        title: "Best Practices to Avoid Missed Orders",
        content: `Ensure you never miss an order:

**Notification Setup:**
1. Enable push notifications
2. Enable email notifications
3. Enable SMS alerts (if available)
4. Check notification settings regularly
5. Test notifications work

**Workflow Best Practices:**
1. Check orders page regularly (every 15 min)
2. Keep browser tab open during business hours
3. Assign order monitoring to specific staff
4. Set up audio alerts for new orders
5. Have backup staff for breaks

**Common Causes of Missed Orders:**
- Notifications disabled/blocked
- App not open during business hours
- Email going to spam
- Staff transition gaps
- Technical issues (browser, network)

**Recovery Actions:**
- Respond immediately to "RESENT" orders
- Acknowledge even if running late
- Communicate delays proactively
- Request customer patience`
      }
    ],
    faqs: [
      {
        question: "What happens if I miss the 5-minute acknowledgment window?",
        answer: "The order is resent with an URGENT flag. You get another 5 minutes. If you miss that too, the order auto-cancels and the customer is refunded."
      },
      {
        question: "Can I change the ready-by time after acknowledging?",
        answer: "No. Once acknowledged, the ready-by time is locked. Always select a realistic time you can meet."
      },
      {
        question: "When does payment capture happen?",
        answer: "Payment is captured (charged to customer) when you click 'Mark Ready'. Until then, it's only a hold."
      },
      {
        question: "What if a customer doesn't pick up their order?",
        answer: "Hold the order for 24 hours. If unclaimed, you may dispose of perishables. Contact support for refund processing."
      }
    ]
  },

  // Priority 4: Onboarding & Configuration
  "onboarding-config": {
    id: "retailer-onboarding",
    title: "Onboarding & Configuration",
    icon: "Rocket",
    priority: 4,
    estimatedTime: "10 min read",
    description: "Complete setup guide for new retailers",
    sections: [
      {
        id: "seven-step-wizard",
        title: "7-Step Onboarding Wizard",
        content: `Complete onboarding to go live:

**Step 1: Business Information**
- Legal business name
- Business type (grocery, convenience, etc.)
- Store address (verified via Google Maps)
- Phone number (SMS verification required)
- Primary contact email

**Step 2: Store Hours**
- Daily operating hours
- Timezone selection
- Holiday hours (optional)
- Affects order availability windows

**Step 3: Bank/Payout Details**
- Option A: Stripe Connect (recommended)
- Option B: Manual bank entry
- Required for receiving payments
- Verification may take 1-3 days

**Step 4: Document Verification (Optional)**
- Business license
- Sales tax certificate
- Food handling permits
- Speeds up approval process

**Step 5: Store Logo (Optional)**
- Brand image for your store
- Displayed on consumer-facing pages
- Recommended: 400x400px minimum
- Formats: PNG, JPG, WEBP

**Step 6: Delivery Configuration**
- Prep time setting (default: 30 min)
- Delivery radius
- Pickup enabled/disabled
- Delivery enabled/disabled

**Step 7: Review & Submit**
- Summary of all information
- Verification checklist
- Submit for admin approval
- Typical approval: 24-48 hours`
      },
      {
        id: "stripe-connect",
        title: "Stripe Connect Integration",
        content: `Setting up payments:

**What is Stripe Connect?**
- Secure payment processing
- Direct deposits to your bank
- No DealShaq access to your funds
- Industry-standard security

**Setup Process:**
1. Click "Connect with Stripe" in Step 3
2. Redirected to Stripe's secure site
3. Enter business details
4. Verify identity (may require ID)
5. Link bank account
6. Return to DealShaq

**Required Information:**
- Business EIN or SSN (sole prop)
- Business address
- Bank routing number
- Bank account number
- Government ID (for verification)

**Verification Timeline:**
- Instant: Most businesses
- 1-3 days: Additional verification needed
- Contact support if delayed

**After Connection:**
- Payments auto-deposit weekly
- View transaction history in Stripe
- DealShaq shows pending amounts
- No action needed for each order`
      },
      {
        id: "delivery-config",
        title: "Delivery & Pickup Configuration",
        content: `Configure fulfillment options:

**Prep Time Setting**
- Time needed to prepare typical order
- Options: 15, 20, 30, 45, 60, 90 minutes
- Affects earliest available times for customers
- Be realistic to avoid overdue orders

**Delivery Radius**
- How far you'll deliver (or DoorDash will)
- Typically 3-10 miles
- Larger radius = more customers
- Consider delivery time and food quality

**Pickup Configuration**
- Enable/disable pickup option
- Pickup location instructions
- Parking information
- Hours may differ from delivery

**Delivery Configuration**
- Enable/disable delivery option
- Handled by DoorDash Drive
- You dispatch when order is ready
- Customer pays delivery fee separately

**Recommended Settings:**
| Store Type | Prep Time | Radius |
|------------|-----------|--------|
| Small grocery | 30 min | 5 miles |
| Convenience | 15 min | 3 miles |
| Large grocery | 45 min | 7 miles |
| Specialty | 20 min | 10 miles |`
      },
      {
        id: "sandbox-vs-live",
        title: "Sandbox vs Live Mode",
        content: `Understanding your store status:

**Sandbox Mode (Pre-Approval)**
- Default for new stores
- Post items for testing
- Orders are simulated
- No real payments processed
- Dashboard shows test data

**How to Identify:**
- Yellow "Sandbox" banner on dashboard
- Items marked as "Test"
- Can't receive real orders

**Going Live Checklist:**
1. ✅ Complete all 7 onboarding steps
2. ✅ Stripe Connect verified
3. ✅ Admin approval received
4. ✅ At least 2 RSHDs posted
5. ✅ Store hours configured

**After Going Live:**
- Banner changes to green "Live"
- Real orders start coming
- Real payments processed
- Analytics show actual data

**Reverting to Sandbox:**
- Contact support if needed
- Use for testing new features
- No impact on customer-facing store`
      },
      {
        id: "scheduled-reports",
        title: "Scheduled Reports",
        content: `Automate your reporting:

**Available Reports:**
- Daily sales summary
- Weekly performance digest
- Monthly analytics export
- Inventory alerts digest

**Setting Up Reports:**
1. Go to Settings → Reports
2. Select report type
3. Choose frequency (daily, weekly, monthly)
4. Enter email recipients
5. Set preferred delivery time

**Report Contents:**

*Daily Summary:*
- Orders received/completed
- Revenue
- Top selling items
- Cancellation rate

*Weekly Digest:*
- Week-over-week comparison
- Trending categories
- Customer feedback summary
- Inventory turnover

*Monthly Analytics:*
- Detailed performance metrics
- Category breakdown
- Customer demographics
- Recommendations

**Export Formats:**
- PDF (formatted report)
- CSV (raw data)
- Both available for all reports`
      }
    ],
    faqs: [
      {
        question: "How long does approval take?",
        answer: "Typically 24-48 hours. Having all documents ready speeds up the process."
      },
      {
        question: "Can I operate without Stripe Connect?",
        answer: "Yes, you can enter bank details manually, but payments may take longer to process (3-5 business days instead of weekly auto-deposit)."
      },
      {
        question: "What if my store has multiple locations?",
        answer: "Each location needs a separate DealShaq account. Contact support for multi-location management features."
      },
      {
        question: "Can I change my store hours after going live?",
        answer: "Yes! Go to Settings → Store Hours. Changes take effect immediately."
      }
    ]
  },

  // Priority 5: Tax & Compliance (Virginia Marketplace Facilitator)
  "tax-compliance": {
    id: "retailer-tax-compliance",
    title: "Tax & Compliance (Virginia Marketplace Facilitator)",
    icon: "FileText",
    priority: 5,
    estimatedTime: "9 min read",
    description: "How DealShaq collects and remits sales tax for you, plus catalog, API, and webhook setup",
    sections: [
      {
        id: "mf-overview",
        title: "Marketplace Facilitator Overview",
        content: `Virginia law requires a marketplace facilitator to calculate, collect, and remit sales tax on the sales it facilitates. **DealShaq acts as the marketplace facilitator** for every order placed through the platform.

**What this means for you (the DRLP):**
- Sales tax is calculated per line item at checkout, collected from the consumer, and remitted to Virginia by DealShaq.
- You do **not** file Virginia sales tax for DealShaq-facilitated orders.
- Every facilitated order is written to an immutable tax transaction log for audit purposes.
- You remain responsible for your own direct (non-DealShaq) sales.

**Scope (Phase 1):** Virginia only, including Northern Virginia (NoVA) locality rates.`
      },
      {
        id: "catalog-tax-categories",
        title: "Catalog Tax Categories",
        content: `When you push your catalog to DealShaq, include a **tax category** on each item so the tax engine applies the correct Virginia rate.

**Why it matters:**
- Grocery food is taxed at Virginia's reduced rate.
- Prepared foods and general merchandise are taxed at the general rate plus any applicable locality (e.g., NoVA) rate.
- Items with no category fall back to a default and may be taxed less accurately.

**How to send it:**
- Endpoint: \`POST /api/drlp/catalog/update\`
- Include a tax category field on each item in the \`items\` array.
- Update categories whenever your assortment changes.

**Best practice:** Set an explicit tax category on every SKU rather than relying on the fallback.`
      },
      {
        id: "worked-examples",
        title: "Worked Tax Examples (NoVA)",
        content: `Northern Virginia (NoVA) rates used by the engine:

| Tax category | Rate |
|--------------|------|
| Grocery food / personal hygiene | 1% |
| Prepared food / general merchandise | 6% |
| Delivery fee (when basket has a taxable line) | 6% |
| Exempt (SNAP/EBT-paid, admin-exempt) | 0% |
| Charity round-up | Never taxed |

Tax is computed **per line**, rounded to the cent (ROUND_HALF_UP), then summed.

**Example 1 — Grocery item (1%)**
- Organic Milk, qty 2 @ $3.00 = **$6.00** extended
- Tax = $6.00 × 1% = **$0.06**

**Example 2 — Prepared food (6%)**
- Rotisserie Chicken (prepared), qty 1 @ $8.00 = **$8.00** extended
- Tax = $8.00 × 6% = **$0.48**

**Example 3 — Mixed basket with delivery + charity (full order)**
| Line | Qty × Price | Extended | Category | Rate | Line tax |
|------|-------------|----------|----------|------|----------|
| Organic Milk | 2 × $3.00 | $6.00 | Grocery | 1% | $0.06 |
| Rotisserie Chicken | 1 × $8.00 | $8.00 | Prepared | 6% | $0.48 |
| **Item subtotal** | | **$14.00** | | | **$0.54** |
| Delivery fee | | $2.00 | General | 6% | $0.12 |
| Charity round-up | | $0.34 | — | 0% | $0.00 |

- **Order tax total** = $0.54 + $0.12 = **$0.66**
- **Order total** = $14.00 subtotal + $0.66 tax + $2.00 delivery + $0.34 charity = **$17.00**

**Notes:**
- The delivery fee is only taxed (at 6%) when the basket contains at least one taxable line.
- SNAP/EBT-eligible items paid with EBT are exempt (0%), and the charity round-up is never taxed.`
      },
      {
        id: "api-key-setup",
        title: "DRLP API Key Setup",
        content: `DealShaq issues each DRLP a private API key so your systems can push catalog, inventory, and pricing data.

**Getting your key:**
- Your DealShaq admin generates it from the DRLP API admin screen.
- The key (format \`dsk_live_...\`) is shown **once** — store it securely.

**Using your key:**
Send it on every ingestion request as a header:
\`\`\`
X-DRLP-API-Key: dsk_live_xxxxxxxxxxxxxxxx
\`\`\`

**Ingestion endpoints:**
- \`POST /api/drlp/catalog/update\` — products + tax categories
- \`POST /api/drlp/inventory/update\` — stock levels
- \`POST /api/drlp/pricing/update\` — prices
- \`POST /api/drlp/store-metadata/update\` — store name, address, ZIP, locality

**Security:** Keep the key secret. Contact your DealShaq admin to rotate or revoke it if it is ever exposed.`
      },
      {
        id: "webhook-setup",
        title: "Webhook Setup",
        content: `Webhooks let DealShaq push order, refund, and reconciliation events back to your systems in real time.

**Configure (Retailer Settings → webhook config):**
1. Set your **webhook base URL**.
2. Set a shared **HMAC secret**.
3. Enable push.
4. Run the **test ping** to verify — a successful signed ping marks your webhook as verified.

**Verifying authenticity:**
Every event is signed with **HMAC-SHA256** using your shared secret. Recompute the signature on your side and compare before trusting a payload.

**Delivery & retries:** Failed deliveries are retried with backoff. You can review delivery history in the webhook delivery log.`
      },
      {
        id: "reconciliation",
        title: "Reconciliation Logs",
        content: `DealShaq generates **daily** and **monthly** reconciliation logs so you can reconcile payouts and tax.

**Each log includes (per store):**
- Gross sales
- Tax collected
- Refund total and refund tax
- Transaction count
- An audit checksum for tamper-evidence

**Access:**
- View in the Retailer Reconciliation screen.
- Download as CSV for your records.
- Logs are idempotent and stable once generated for a closed period.`
      },
      {
        id: "st1-posture",
        title: "ST-1 Filing Posture",
        content: `DealShaq files Virginia's **ST-1 / ST-1A** sales-tax returns for facilitated sales on your behalf.

**Current posture (Phase 1):**
- Runs in **TEST mode** with an **iFile (manual upload)** posture.
- DealShaq generates the authoritative ST-1 / ST-1A files each period and uploads them to Virginia Tax manually.
- Automated electronic submission will be enabled once Virginia approves DealShaq as a registered software provider.

**Your takeaway:** You do not need to file ST-1 for DealShaq-facilitated orders — DealShaq handles calculation, collection, remittance, and filing.`
      }
    ],
    faqs: [
      {
        question: "Do I still file Virginia sales tax for DealShaq orders?",
        answer: "No. As the marketplace facilitator, DealShaq collects and remits Virginia sales tax for orders facilitated on the platform. You remain responsible only for your own direct (non-DealShaq) sales."
      },
      {
        question: "Why does my catalog need tax categories?",
        answer: "So the tax engine applies the correct Virginia rate — the reduced grocery rate vs. the general rate plus locality. Items without a category use a default and may be less accurate."
      },
      {
        question: "How do I get and use my API key?",
        answer: "Your DealShaq admin issues it (shown once). Send it on every ingestion request as the X-DRLP-API-Key header. Contact support to rotate or revoke it."
      },
      {
        question: "Where can I see how much tax was collected?",
        answer: "In your Reconciliation logs (daily and monthly), downloadable as CSV, with per-store tax totals and an audit checksum."
      }
    ]
  }
};

// =============================================================================
// CONSUMER PRIORITY COVERAGE AREAS (4)
// =============================================================================
export const CONSUMER_PRIORITY_DOCS = {
  // Priority 1: DealShaq Shopping Assistant (DSA)
  "dealshaq-agent": {
    id: "consumer-dealshaq-agent",
    title: "DealShaq Shopping Assistant (DSA) (AI Shopping Assistant)",
    icon: "Bot",
    priority: 1,
    estimatedTime: "12 min read",
    description: "Master the AI-powered shopping assistant",
    sections: [
      {
        id: "agent-overview",
        title: "What is the DealShaq Shopping Assistant (DSA)?",
        content: `Your AI-powered shopping companion:

**What It Does:**
- Understands natural language requests
- Finds deals matching your needs
- Plans meals and suggests recipes
- Remembers your preferences
- Sources from multiple stores
- Handles dietary restrictions

**How It's Different:**
- Not just a search box
- Understands context ("something for dinner")
- Remembers conversation history
- Suggests alternatives when items unavailable
- Can plan multi-item shopping trips

**Example Requests:**
- "I need ingredients for tacos for 4 people"
- "Find me halal chicken under $10"
- "What can I make with these ingredients?"
- "Organic produce on sale near me"
- "Plan a week of keto meals"`
      },
      {
        id: "shopping-list-sourcing",
        title: "Shopping List Sourcing (Core Workflow)",
        content: `DealShaq's AI Agent can take a full grocery shopping list, apply your constraints, and source the best available items across preferred stores. This is a core workflow of the Consumer experience.

**Submitting a Shopping List:**
Provide your list in any format:

*Typing Your List:*
- Natural language: "I need groceries for the week."
- Plain text list: "eggs, milk, bread, chicken thighs, rice"
- Bullet or numbered lists
- Mixed formats combining lists and instructions
- Partial lists that can be expanded later

*Uploading Your List:*
Click the **Upload** button to attach a file:
- **Text files** (.txt) - Plain text shopping lists
- **CSV files** (.csv) - Spreadsheet format lists
- **Images** (.jpg, .png, .webp) - Photos of handwritten lists or receipts
- **PDF files** (.pdf) - Scanned or digital lists

The AI Agent uses OCR to extract items from images and PDFs automatically.

**Adding Constraints:**
Constraints can be provided before, during, or after submitting your list:
- **Budget limits**: "Keep the total under $50."
- **Dietary restrictions**: halal, kosher, vegan, gluten-free, organic, low sodium
- **Store preferences**: "Only from Walmart and Safeway."
- **Brand preferences**: "Prefer organic brands."
- **Freshness rules**: "Items must last at least 5 days."
- **Substitution rules**: allow, disallow, or restrict substitutions

**How DealShaq Sources Your List:**
Once you provide your list and constraints, the agent:
1. Searches your preferred stores first
2. Expands to nearby stores when needed
3. Optimizes for best value, fewest stores, or balanced
4. Handles unavailable items with substitutions
5. Applies all constraints (budget, dietary, brand, freshness)
6. Builds a sourced list grouped by store

**Reviewing the Sourced List:**
The agent presents:
- Items grouped by store
- Substitution suggestions with reasons
- Savings summary
- Constraint compliance status`
      },
      {
        id: "shopping-list-approval",
        title: "Approving & Finalizing Your List",
        content: `After the agent sources your list, you have full control:

**Approving or Modifying:**
- Approve the entire list at once
- Approve or reject individual items
- Add more items to the list
- Change constraints mid-process
- Request re-optimization:
  - "Make it cheaper"
  - "Minimize store count"
  - "Switch to all organic"

**Final Cart Creation:**
Once approved:
- Items are added to your cart (single or multi-store)
- You can still edit before checkout
- Delivery or pickup options presented
- Checkout proceeds normally

**Example Conversations:**

*Example 1: Simple List*
> **You**: "I need eggs, milk, bread, and chicken. Keep it under $40."
> **Agent**: "Got it. I'll source these items under $40."

*Example 2: Weekly Groceries with Constraints*
> **You**: "I need a full week of groceries for 4 people. Everything must be halal. Prefer Walmart and Safeway. Keep the total under $120."
> **Agent**: "Understood. Sourcing a halal-compliant weekly grocery list under $120 from Walmart and Safeway."

*Example 3: Handling Substitutions*
> **You**: "Spinach is fine if kale is unavailable."
> **Agent**: "Kale is unavailable at your preferred stores. I've substituted spinach."`
      },
      {
        id: "recipes-meal-planning",
        title: "Recipes & Meal Planning",
        content: `Let the agent plan your meals:

**Requesting Recipes:**
- "Give me a recipe for pasta dinner"
- "12-course halal feast for 8 people"
- "Quick weeknight dinner ideas"
- "Kid-friendly lunch options"

**How It Works:**
1. You describe what you want
2. Agent suggests recipes
3. Shows available ingredients from deals
4. Highlights missing items
5. Offers to search other stores

**Multi-Course Meals:**
- Request specific course count
- Agent plans each course
- Balances variety and availability
- Considers dietary restrictions throughout

**Budget-Aware Planning:**
- Specify budget ("under $50")
- Agent optimizes for savings
- Prioritizes highest discounts
- Suggests substitutions to save more`
      },
      {
        id: "dietary-preferences",
        title: "Dietary Preferences",
        content: `The agent understands all major dietary needs:

**Supported Restrictions:**
- Halal
- Kosher
- Vegan
- Vegetarian
- Gluten-free
- Dairy-free
- Nut-free
- Keto
- Paleo
- Low-sodium

**How to Use:**
- State in your request: "vegan options"
- Or set in your profile (Settings)
- Agent remembers for future requests

**How It Filters:**
- Checks product attributes
- Reviews ingredient lists
- Flags potential conflicts
- Asks for clarification if unsure

**Strictness Levels:**
- Some restrictions are absolute (allergies)
- Others are preferences (organic)
- Agent prioritizes safety over convenience`
      },
      {
        id: "multi-store-sourcing",
        title: "Multi-Store Sourcing",
        content: `Get the sizzling hot deals across stores:

**Preferred Stores (DACDRLP):**
- Set your favorite stores in Settings
- Agent searches these first
- Optimizes for convenience

**Expanded Search:**
- When items not in preferred stores
- Agent offers to search nearby stores
- You control if/when this happens

**"Search Other Stores" Flow:**
1. Agent can't find all items
2. Shows what's missing
3. Offers: "Search 5 stores within 3 miles?"
4. You accept or decline
5. Results combined intelligently

**Store Priority:**
1. Your preferred stores (DACDRLP)
2. Nearby stores you've used before
3. All stores within radius
4. Extended radius (with consent)`
      },
      {
        id: "memory-system",
        title: "Memory System",
        content: `The agent remembers your preferences:

**Short-Term Memory:**
- Current conversation context
- Recent requests
- Items discussed
- Resets after session ends

**Long-Term Memory:**
- Dietary preferences
- Budget patterns
- Favorite categories
- Store preferences
- Persists across sessions

**What Gets Remembered:**
- "I'm vegetarian" → Saved
- "I usually spend $50/week" → Learned
- "I prefer organic" → Noted
- Frequent purchases → Tracked

**Privacy:**
- You control your data
- Clear memory anytime
- No data sold to third parties
- See what's stored in Settings`
      },
      {
        id: "clearing-memory",
        title: "Clearing Memory",
        content: `Reset your AI preferences:

**How to Clear Memory:**
1. Open DealShaq Shopping Assistant (DSA)
2. Click the "Memory" button
3. View what's been learned
4. Choose reset type
5. Confirm action

**Reset Options:**

*Clear Short-Term Only:*
- Resets current conversation
- Keeps learned preferences
- Like starting fresh today

*Clear Long-Term Only:*
- Removes learned preferences
- Keeps current conversation
- Good for changed dietary needs

*Clear All:*
- Complete reset
- Fresh start
- Agent knows nothing about you

**When to Clear:**
- Diet change (no longer vegetarian)
- Different household member using account
- Privacy concerns
- Testing different preferences`
      },
      {
        id: "conversation-examples",
        title: "Conversation Examples",
        content: `Learn by example:

**Example 1: Simple Request**
\`\`\`
You: "I need eggs"
Agent: "I found 3 egg deals near you:
       1. Organic Large Eggs - $3.99 (50% off) at Fresh Mart
       2. Free-Range Eggs - $4.50 (60% off) at Green Grocer
       Would you like to add any to your cart?"
\`\`\`

**Example 2: Meal Planning**
\`\`\`
You: "Plan dinner for 4, we're vegetarian, budget $30"
Agent: "I suggest a Mediterranean feast:
       - Falafel wraps with tahini
       - Greek salad
       - Hummus and pita
       
       Available from DealShaq partners:
       ✓ Chickpeas - $1.99 at Store A
       ✓ Pita bread - $2.50 at Store A
       ✓ Mixed greens - $3.00 at Store B
       ...
       
       Total: $24.50 | You save: $18.00
       
       Shall I add these to your cart?"
\`\`\`

**Example 3: Dietary Restriction**
\`\`\`
You: "Halal chicken options"
Agent: "I found 2 halal-certified chicken deals:
       1. Halal Chicken Breast - $6.99/lb (60% off) at Halal Market
       2. Halal Whole Chicken - $8.99 (50% off) at Fresh Halal
       
       Both are certified halal. Add to cart?"
\`\`\`

**Example 4: Substitution**
\`\`\`
You: "I need milk but I'm lactose intolerant"
Agent: "I found these dairy-free alternatives:
       1. Oat Milk - $3.50 (50% off)
       2. Almond Milk - $2.99 (60% off)
       3. Coconut Milk - $3.25 (50% off)
       
       Would any of these work?"
\`\`\``
      }
    ],
    faqs: [
      {
        question: "Does the agent understand other languages?",
        answer: "Yes! The agent supports English, Spanish, Arabic, Chinese, and Korean. Just write in your preferred language."
      },
      {
        question: "Why does the agent ask for confirmation?",
        answer: "For purchases or searches that affect inventory, the agent confirms to ensure it understood correctly and to prevent accidental orders."
      },
      {
        question: "Can I turn off the memory feature?",
        answer: "Yes. Go to Settings → Privacy → Disable AI Memory. The agent will still work but won't remember preferences."
      },
      {
        question: "What if the agent misunderstands me?",
        answer: "Simply rephrase your request or say 'No, I meant...' The agent learns from corrections."
      }
    ]
  },

  // Priority 2: Shopping & Checkout
  "shopping-checkout": {
    id: "consumer-shopping-checkout",
    title: "Shopping & Checkout Flow",
    icon: "ShoppingCart",
    priority: 2,
    estimatedTime: "7 min read",
    description: "Complete guide to finding and purchasing deals",
    sections: [
      {
        id: "browsing-deals",
        title: "Browsing Deals",
        content: `Find the best savings near you:

**Browse Page Features:**
- Category filters (20 categories)
- Distance filter
- Price range filter
- Discount level filter
- Search by keyword

**Deal Card Information:**
- Product name and image
- Original price (crossed out)
- Deal price (highlighted)
- Discount percentage
- Store name and distance
- Stock level indicator

**Sorting Options:**
- Nearest first
- Highest discount
- Lowest price
- Newest arrivals

**Using Search:**
- Enter product names
- Brand names work too
- Category keywords
- Dietary terms ("organic", "halal")`
      },
      {
        id: "deal-badges",
        title: "Understanding Deal Badges",
        content: `What the badges mean:

**Discount Badges:**
- <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> **Sizzling** (orange): 50% off
- <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> **Hot** (red): 60% off
- <img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"><img src="/branded-flame.svg" width="14" height="14" alt="DealShaq" style="vertical-align:text-bottom"> **Sizzling Hot** (flame): 75% off

**Stock Badges:**
- ✅ **In Stock**: Good availability
- ⚠️ **Low Stock**: Under 5 remaining
- 🔴 **Sold Out**: Currently unavailable

**Special Badges:**
- 🌱 **Organic**: Certified organic
- ☪️ **Halal**: Halal certified
- ✡️ **Kosher**: Kosher certified
- 🌿 **Vegan**: 100% plant-based
- 🚫🌾 **Gluten-Free**: No gluten

**Time Badges:**
- ⏰ **Ending Soon**: Deal expires within 24 hours
- 🆕 **New**: Posted within last 4 hours
- ⚡ **Flash Deal**: Limited-time extra savings`
      },
      {
        id: "adding-to-cart",
        title: "Adding Items to Cart",
        content: `Build your shopping cart:

**From Browse Page:**
1. Find item you want
2. Click "Add to Cart" button
3. Select quantity (if multiple available)
4. Item added with confirmation

**From Deal Detail:**
1. Click on deal card
2. View full details
3. Check store information
4. Select quantity
5. Click "Add to Cart"

**From DealShaq Shopping Assistant (DSA):**
1. Agent suggests items
2. Say "Add to cart" or click button
3. Confirm selection
4. Continue shopping or checkout

**Cart Behavior:**
- Items grouped by store
- Each store = separate transaction
- Delivery calculated per store
- Can mix pickup and delivery`
      },
      {
        id: "stock-levels",
        title: "Stock Levels",
        content: `Understanding availability:

**Real-Time Inventory:**
- Stock updates live as orders placed
- "Live" indicator shows real-time connection
- Refresh if unsure

**Stock Statuses:**

| Status | Meaning | Action |
|--------|---------|--------|
| In Stock (10+) | Good availability | Order anytime |
| Low Stock (1-9) | Limited remaining | Order soon |
| Only X left | Very limited | Order now |
| Sold Out | None available | Check back later |

**When Item Sells Out:**
- While browsing: Card shows "Sold Out"
- In cart: Warning displayed
- During checkout: Item removed, notified

**Tips:**
- Popular items sell fast
- Check stock before checkout
- Don't wait too long on low stock
- Set alerts for favorites`
      },
      {
        id: "delivery-vs-pickup",
        title: "Delivery vs Pickup",
        content: `Choose your fulfillment method:

**Pickup Option:**
- Go to store to collect
- Usually ready in 2-4 hours
- No delivery fee
- Show confirmation at counter
- Best for: Large orders, nearby stores

**Delivery Option:**
- DoorDash delivers to you
- Delivery fee varies by distance
- Real-time tracking
- Leave at door or hand off
- Best for: Convenience, far stores

**Scheduled Delivery:**
- Choose specific time slot
- Plan around your schedule
- 30-min to 2-hour windows
- Available up to 3 days ahead

**ASAP Delivery:**
- Fastest available time
- Usually 30-60 minutes
- Depends on store prep time
- Subject to driver availability`
      },
      {
        id: "payment-flow",
        title: "Payment Flow",
        content: `Completing your purchase:

**Accepted Payment Methods:**
- Credit cards (Visa, MC, Amex, Discover)
- Debit cards
- Apple Pay
- Google Pay

**Checkout Steps:**
1. Review cart items
2. Select delivery/pickup for each store
3. Enter/confirm delivery address
4. Choose payment method
5. Review total (items + tax + delivery)
6. Place order

**Payment Security:**
- Processed by Stripe
- PCI-compliant
- Card details never stored by DealShaq
- Payment held until order ready

**Order Confirmation:**
- Confirmation number displayed
- Email sent immediately
- Push notification (if enabled)
- Track status in Orders page`
      },
      {
        id: "out-of-stock-handling",
        title: "Handling Out-of-Stock Items",
        content: `What happens when items sell out:

**During Browsing:**
- "Sold Out" badge displayed
- Cannot add to cart
- Check "Notify Me" option

**While in Cart:**
- Warning icon appears
- Item quantity may reduce
- Remove or continue with available

**During Checkout:**
- Final availability check
- Unavailable items flagged
- Option to proceed without
- Automatic refund if partial

**After Order Placed:**
- Store may contact for substitution
- Accept, decline, or cancel
- Partial refund for unavailable items

**Preventing Issues:**
- Order quickly when low stock
- Set alerts for favorites
- Check cart before checkout
- Don't leave items in cart overnight`
      }
    ],
    faqs: [
      {
        question: "Can I combine items from different stores?",
        answer: "Yes, but each store is a separate order with its own delivery fee. The app groups them for you automatically."
      },
      {
        question: "Why did an item disappear from my cart?",
        answer: "Items are removed when they sell out. Cart contents aren't reserved until checkout is complete."
      },
      {
        question: "Can I change my order after placing it?",
        answer: "Within 5 minutes of placing, you can cancel. After that, contact the store directly through the app."
      },
      {
        question: "Are there minimum order amounts?",
        answer: "No minimum for pickup. For delivery, some stores may have a minimum (shown at checkout)."
      }
    ]
  },

  // Priority 3: Store Discovery
  "store-discovery": {
    id: "consumer-store-discovery",
    title: "Store Discovery & Multi-Store Shopping",
    icon: "MapPin",
    priority: 3,
    estimatedTime: "6 min read",
    description: "Find and shop from nearby stores",
    sections: [
      {
        id: "preferred-stores",
        title: "Setting Preferred Stores",
        content: `Optimize your shopping experience:

**What Are Preferred Stores?**
- Stores you shop at regularly
- Get priority in search results
- AI agent checks these first
- Faster, more relevant recommendations

**How to Set Preferred Stores:**
1. Go to Settings → Preferred Stores
2. Browse nearby stores
3. Click ❤️ to add as preferred
4. Drag to reorder priority
5. Remove with X button

**Benefits:**
- Personalized deal feed
- Agent searches here first
- Quicker checkout (saved info)
- Better recommendations

**Recommendations:**
- Add 3-5 preferred stores
- Include different store types
- Balance proximity and selection
- Update as habits change`
      },
      {
        id: "browsing-stores",
        title: "Browsing Nearby Stores",
        content: `Explore what's around you:

**Store Directory:**
- List of all partner stores
- Sorted by distance
- Filter by store type
- Search by name

**Store Profile Shows:**
- Store name and logo
- Address and distance
- Operating hours
- Number of active deals
- Customer rating
- Delivery/pickup options

**Store Page Features:**
- All current deals
- Store information
- Contact details
- Directions link
- Favorite button`
      },
      {
        id: "switching-stores",
        title: "Switching Between Stores",
        content: `Shop multiple stores efficiently:

**While Browsing:**
- Deals show store name
- Click store name to see all their deals
- "Back to all deals" returns to mixed view

**In Cart:**
- Items grouped by store
- Each store section expandable
- Can modify each separately
- Checkout processes all

**During Agent Conversation:**
- Agent notes which store for each item
- Can request specific store
- "Search Store X for eggs"
- Handles multi-store automatically`
      },
      {
        id: "multi-store-carts",
        title: "Multi-Store Carts",
        content: `Managing orders from multiple stores:

**How It Works:**
- Single cart, multiple stores
- Items automatically grouped
- Separate checkout per store
- Individual delivery tracking

**Example Cart:**
\`\`\`
🛒 Your Cart (5 items from 2 stores)

📍 Fresh Mart (3 items) - Pickup
   - Organic Eggs - $3.99
   - Milk - $2.50
   - Bread - $1.99
   Subtotal: $8.48
   
📍 Green Grocer (2 items) - Delivery
   - Avocados - $4.00
   - Spinach - $2.99
   Subtotal: $6.99 + $3.99 delivery

Total: $19.46
\`\`\`

**Tips:**
- Consolidate same-store items
- Consider pickup to save on delivery
- Check all store sections before checkout`
      },
      {
        id: "delivery-fees",
        title: "Understanding Delivery Fees",
        content: `How delivery pricing works:

**Fee Structure:**
- Base fee: $1.99 - $3.99 (varies by store)
- Distance fee: $0.50 per mile over 2 miles
- Service fee: 10% (capped at $5)
- Tip: Optional but appreciated

**Free Delivery:**
- Some stores offer free delivery over $X
- Promotions may waive fees
- Check store page for offers

**Pickup = No Delivery Fee:**
- Always free for pickup
- Prepare to show ID at store
- Items held for 24 hours

**Comparing Options:**
- App shows total with fees
- Compare pickup vs delivery
- Factor in your time value`
      },
      {
        id: "radar-map",
        title: "Deal Radar & Map",
        content: `Visualize deals around you:

**Deal Radar:**
- Concentric circles from your location
- Dots represent stores with deals
- Size = number of deals
- Color = discount level

**Map View:**
- Full map of area
- Store pins with deal counts
- Click pin to see store deals
- Filter by category/discount

**Using the Map:**
1. Enable location services
2. Click "Map" icon in navigation
3. Pan and zoom to explore
4. Click store pins for details
5. Click deal for full info

**Tips:**
- Zoom out to see more options
- Zoom in for detail in dense areas
- Use filters to reduce clutter
- Plan routes for multiple stores`
      }
    ],
    faqs: [
      {
        question: "How does the app know my location?",
        answer: "When you enable location services, the app uses GPS. You can also manually enter an address in Settings."
      },
      {
        question: "Can I order from stores outside my area?",
        answer: "You can browse any store, but delivery is limited by distance. Pickup is always available if you can get there."
      },
      {
        question: "Why do delivery fees vary?",
        answer: "Fees depend on store policy, distance, and DoorDash pricing. Each store sets their own base fee."
      },
      {
        question: "What if a store isn't on DealShaq?",
        answer: "Suggest them via Settings → Suggest a Store. We're always adding new partners."
      }
    ]
  },

  // Priority 4: Orders & Tracking
  "orders-tracking": {
    id: "consumer-orders-tracking",
    title: "Orders, Tracking & Notifications",
    icon: "Package",
    priority: 4,
    estimatedTime: "6 min read",
    description: "Track orders and manage notifications",
    sections: [
      {
        id: "order-statuses",
        title: "Order Statuses Explained",
        content: `Understanding your order progress:

**Status Flow:**
\`\`\`
Placed → Confirmed → Preparing → Ready → Out for Delivery → Delivered
                                      ↘ Ready for Pickup
\`\`\`

**Status Details:**

| Status | Meaning | Your Action |
|--------|---------|-------------|
| Placed | Order submitted | Wait for confirmation |
| Confirmed | Store accepted | Payment captured |
| Preparing | Store packing items | Track progress |
| Ready | Items prepared | Pickup or await driver |
| Out for Delivery | Driver has order | Track location |
| Delivered | Complete! | Enjoy your savings |
| Ready for Pickup | Come get it! | Go to store |
| Cancelled | Order cancelled | Check reason/refund |`
      },
      {
        id: "real-time-tracking",
        title: "Real-Time Tracking",
        content: `Track your delivery live:

**Delivery Tracking:**
- Map shows driver location
- ETA updates in real-time
- Driver name and photo
- Contact driver option

**How to Track:**
1. Go to Orders
2. Click active order
3. View map and status
4. Updates automatically

**Tracking Details:**
- Driver's current location
- Estimated arrival time
- Route visualization
- Status updates

**If Driver is Lost:**
- Use in-app chat
- Provide landmarks
- Update delivery notes
- Contact support if needed`
      },
      {
        id: "notifications",
        title: "Push & Email Notifications",
        content: `Stay informed about your orders:

**Notification Types:**
- Order confirmed
- Being prepared
- Ready for pickup
- Driver assigned
- Out for delivery
- Delivered
- Order cancelled/modified

**Channels:**
- Push notifications (app)
- Email notifications
- SMS (if enabled)

**Configuring Notifications:**
1. Go to Settings
2. Click Notifications
3. Toggle each type on/off
4. Set quiet hours
5. Save preferences

**Recommended Settings:**
- Keep order updates ON
- Marketing optional
- Enable for important orders`
      },
      {
        id: "pickup-instructions",
        title: "Pickup Instructions",
        content: `Collecting your order:

**When Ready:**
- Push notification sent
- Email with details
- Order shows "Ready for Pickup"

**At the Store:**
1. Go to pickup counter/area
2. Show order confirmation
3. May need to show ID
4. Verify items received
5. Sign/confirm pickup

**What to Bring:**
- Phone with order confirmation
- ID (sometimes required)
- Bags (some stores don't provide)

**Pickup Window:**
- Order held for 24 hours
- After that, may be cancelled
- Perishables may have shorter window

**Issues at Pickup:**
- Item missing? Note at counter
- Quality issue? Refuse item
- Store closed? Contact support`
      },
      {
        id: "handling-delays",
        title: "Handling Delays",
        content: `What to do when things go wrong:

**Order Delayed:**
- Check status in app
- May receive notification
- New ETA provided if available
- Contact support if excessive

**Driver Delayed:**
- Track shows real-time updates
- DoorDash notifies of delays
- Option to cancel (free if > X mins late)
- Automatic compensation may apply

**Store Delayed:**
- Store may message you
- Can accept or cancel
- Partial items may ship first
- Full refund if unacceptable

**Your Options:**
1. Wait for updated ETA
2. Contact store via app
3. Cancel for full refund
4. Contact support for help`
      },
      {
        id: "cancellations-refunds",
        title: "Cancellations & Refunds",
        content: `Managing order issues:

**Cancellation Windows:**
- First 5 minutes: Full refund, easy cancel
- Before preparation: Usually approved
- During preparation: May be denied
- After dispatch: Typically not allowed

**How to Cancel:**
1. Go to Orders
2. Select order
3. Click "Cancel Order"
4. Select reason
5. Confirm cancellation

**Refund Timeline:**
- Immediate: Card authorization released
- 3-5 days: Refund posts to account
- Check with bank if delayed

**Partial Refunds:**
- Items unavailable: Auto-refund
- Quality issues: Report for credit
- Missing items: Contact support

**Disputes:**
- Contact support first
- Provide order details
- Include photos if relevant
- Resolution within 24 hours`
      },
      {
        id: "order-history",
        title: "Order History",
        content: `Access your past orders:

**Viewing History:**
1. Go to Orders
2. Toggle "Past Orders"
3. See all completed orders
4. Click for details

**History Shows:**
- Order date and time
- Items and quantities
- Store information
- Total paid
- Delivery/pickup status
- Rating you gave

**Reordering:**
- Click "Reorder" on past order
- Adds same items to cart
- Availability checked
- Prices may have changed

**Exporting:**
- Download receipts
- Email order details
- Useful for expense reports`
      }
    ],
    faqs: [
      {
        question: "What if my order is wrong or missing items?",
        answer: "Report within 24 hours via the order detail page. Take photos if possible. Refunds or credits issued within 24 hours."
      },
      {
        question: "Can I reschedule a delivery?",
        answer: "Once dispatched, no. Before dispatch, you may be able to contact the store to adjust. Better to cancel and reorder."
      },
      {
        question: "Why was my order cancelled?",
        answer: "Common reasons: items sold out, store closed unexpectedly, payment issue. You'll receive a notification with the reason."
      },
      {
        question: "How do I rate my experience?",
        answer: "After delivery/pickup is complete, a rating prompt appears. You can also rate from the order detail page within 7 days."
      }
    ]
  }
};

// =============================================================================
// COMBINED EXPORT
// =============================================================================
import { 
  RETAILER_EXTENDED_DOCS, 
  CONSUMER_EXTENDED_DOCS, 
  ADMIN_DOCS, 
  CHARITY_DOCS 
} from './documentationExtended';

// Merge priority docs with extended docs
export const RETAILER_ALL_DOCS = {
  ...RETAILER_PRIORITY_DOCS,
  ...RETAILER_EXTENDED_DOCS
};

export const CONSUMER_ALL_DOCS = {
  ...CONSUMER_PRIORITY_DOCS,
  ...CONSUMER_EXTENDED_DOCS
};

export const ALL_DOCUMENTATION = {
  portals: PORTAL_OVERVIEWS,
  glossary: GLOSSARY,
  retailer: RETAILER_ALL_DOCS,
  consumer: CONSUMER_ALL_DOCS,
  admin: ADMIN_DOCS,
  charity: CHARITY_DOCS,
  multilingual: MULTILINGUAL_DOCS
};

// Helper function to search across all documentation
export const searchDocumentation = (query, portal = null) => {
  if (!query || query.length < 2) return [];
  
  const results = [];
  const lowerQuery = query.toLowerCase();
  
  // Search glossary
  Object.values(GLOSSARY).forEach(term => {
    if (
      term.term.toLowerCase().includes(lowerQuery) ||
      term.definition.toLowerCase().includes(lowerQuery)
    ) {
      results.push({
        type: 'glossary',
        title: term.term,
        description: term.definition,
        category: term.category,
        portal: 'shared'
      });
    }
  });
  
  // Helper to search a doc set
  const searchDocs = (docs, portalName) => {
    Object.values(docs).forEach(doc => {
      doc.sections.forEach(section => {
        if (
          section.title.toLowerCase().includes(lowerQuery) ||
          section.content.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            type: 'section',
            docId: doc.id,
            docTitle: doc.title,
            sectionId: section.id,
            title: section.title,
            description: section.content.substring(0, 150) + '...',
            portal: portalName
          });
        }
      });
      // Search FAQs
      doc.faqs?.forEach(faq => {
        if (
          faq.question.toLowerCase().includes(lowerQuery) ||
          faq.answer.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            type: 'faq',
            docId: doc.id,
            docTitle: doc.title,
            title: faq.question,
            description: faq.answer,
            portal: portalName
          });
        }
      });
    });
  };
  
  // Search retailer docs
  if (!portal || portal === 'retailer') {
    searchDocs(RETAILER_ALL_DOCS, 'retailer');
  }
  
  // Search consumer docs
  if (!portal || portal === 'consumer') {
    searchDocs(CONSUMER_ALL_DOCS, 'consumer');
  }
  
  // Search admin docs
  if (!portal || portal === 'admin') {
    searchDocs(ADMIN_DOCS, 'admin');
  }
  
  // Search charity docs
  if (!portal || portal === 'charity') {
    searchDocs(CHARITY_DOCS, 'charity');
  }
  
  return results.slice(0, 20); // Limit results
};

export default ALL_DOCUMENTATION;
